import React, { useState, useEffect } from 'react';
import { apiService } from '../api/apiclient';
import { useNavigate } from 'react-router-dom';

const AuthComponent = ({ onAuthSuccess }) => {
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        const user = localStorage.getItem('user');
        if (user) {
            try {
                const data = JSON.parse(user);
                if (data && data.email) {
                    console.log("Active session found, redirecting...");
                    navigate('/accounts', { replace: true });
                }
            } catch (e) {
                console.error("Invalid session data found");
                localStorage.removeItem('sessionId');
                localStorage.removeItem('user');
            }
        }
    }, [navigate]);

    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    const handleRequestCode = async (e) => {
        e.preventDefault();
        if (countdown > 0) return;

        setLoading(true);
        try {
            await apiService.sendCode(email);
            setStep(2);
            setCountdown(60);
        } catch (err) {
            alert("Failed to send code. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await apiService.login(email, code);
            if (onAuthSuccess) onAuthSuccess(response);
        } catch (err) {
            alert("Invalid or expired code.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full max-w-[360px] mx-auto p-5">
            <h2 className="text-2xl font-bold mb-2 text-center text-gray-900">
                {step === 1 ? 'Sign in to Antares Social' : 'Check your email'}
            </h2>
            <p className="text-sm text-gray-500 mb-8 text-center px-4">
                {step === 1
                    ? 'Enter your email to receive a login code.'
                    : `We sent a 6-digit code to ${email}`}
            </p>

            {step === 1 ? (
                <form onSubmit={handleRequestCode} className="space-y-4">
                    <input
                        type="email"
                        required
                        placeholder="name@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all box-border"
                    />
                    <button
                        type="submit"
                        disabled={loading || countdown > 0}
                        className="w-full py-3 bg-gray-950 text-white rounded-xl font-semibold hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                        {loading ? 'Sending...' : countdown > 0 ? `Resend in ${countdown}s` : 'Continue'}
                    </button>
                </form>
            ) : (
                <form onSubmit={handleVerify} className="space-y-4">
                    <input
                        type="text"
                        required
                        maxLength="6"
                        placeholder="000000"
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 text-center text-xl tracking-[0.5em] font-mono box-border"
                    />
                    <button
                        type="submit"
                        disabled={loading || code.length < 6}
                        className="w-full py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                        {loading ? 'Verifying...' : 'Verify & Login'}
                    </button>
                    <button
                        type="button"
                        onClick={() => setStep(1)}
                        className="w-full py-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
                    >
                        ← Change email
                    </button>
                </form>
            )}

            <p className="mt-8 text-[11px] text-gray-400 text-center leading-relaxed">
                By continuing, you agree to our <br />
                <a href="/termofuse.pdf" className="underline cursor-pointer">Terms of Service</a> and <a href="/privacy" className="underline cursor-pointer">Privacy Policy</a>.
            </p>
        </div>
    );
};

export default AuthComponent;